import type { QueryReviewsRequest } from '@wix/ambassador-devcenter-v1-review/types';
import type { Essentials } from '../../../../../models';
import { isSSR } from '../../../../../utils/is-ssr';
import {
  queryReviews,
  getUserReview as getUserReviewAction,
} from '@wix/ambassador-devcenter-v1-review/http';
import { queryAppReviewsSummary } from '@wix/ambassador-appreviewssummaries-v1-app-reviews-summary/http';
import type { QueryAppReviewsSummaryRequest } from '@wix/ambassador-appreviewssummaries-v1-app-reviews-summary/types';

export const getAllReviews = async ({
  httpClient,
  rpcClient,
  queryReviewsReq,
}: {
  httpClient: Essentials['httpClient'];
  rpcClient?: Essentials['rpcClient'];
  queryReviewsReq: QueryReviewsRequest;
}) => {
  if (isSSR() && rpcClient !== undefined) {
    const queryReviewsRpc = (
      await import('@wix/ambassador-devcenter-v1-review/rpc')
    ).queryReviews;
    return rpcClient
      .request(queryReviewsRpc(queryReviewsReq))
      .then((queryReviewsResponse) => queryReviewsResponse.reviews ?? []);
  }
  return httpClient
    .request(queryReviews(queryReviewsReq))
    .then((queryReviewsResponse) => queryReviewsResponse?.data?.reviews ?? []);
};

export const getUserReview = async ({
  httpClient,
  rpcClient,
  appId,
}: {
  httpClient: Essentials['httpClient'];
  rpcClient?: Essentials['rpcClient'];
  appId: string;
}) => {
  if (isSSR() && rpcClient !== undefined) {
    const getUserReviewRpc = (
      await import('@wix/ambassador-devcenter-v1-review/rpc')
    ).getUserReview;
    return rpcClient
      .request(
        getUserReviewRpc({
          appId,
        }),
      )
      .then((getUserReviewResponse) => getUserReviewResponse.userReview ?? {});
  }
  return httpClient
    .request(
      getUserReviewAction({
        appId,
      }),
    )
    .then(
      (getUserReviewResponse) => getUserReviewResponse.data.userReview ?? {},
    );
};

export const getAppReviewsSummary = async ({
  httpClient,
  rpcClient,
  queryAppReviewsSummaryReq,
}: {
  httpClient: Essentials['httpClient'];
  rpcClient?: Essentials['rpcClient'];
  queryAppReviewsSummaryReq: QueryAppReviewsSummaryRequest;
}) => {
  if (isSSR() && rpcClient !== undefined) {
    const queryAppReviewsSummaryRpc = (
      await import(
        '@wix/ambassador-appreviewssummaries-v1-app-reviews-summary/rpc'
      )
    ).queryAppReviewsSummary;
    return rpcClient
      .request(queryAppReviewsSummaryRpc(queryAppReviewsSummaryReq))
      .then(
        (queryAppReviewsSummaryResponse) =>
          queryAppReviewsSummaryResponse.appReviewsSummaries?.[0] ?? {},
      );
  }
  return httpClient
    .request(queryAppReviewsSummary(queryAppReviewsSummaryReq))
    .then(
      (queryAppReviewsSummaryResponse) =>
        queryAppReviewsSummaryResponse.data.appReviewsSummaries?.[0] ?? {},
    );
};

export const getIsEligibleForReview = async ({
  httpClient,
  appId,
}: {
  httpClient: Essentials['httpClient'];
  appId: string;
}) => {
  if (appId) {
    return httpClient
      .get<{ isEligible: boolean }>(
        `/_serverless/app-market-rpc-proxy/eligible-for-review/${appId}`,
        { withCredentials: true },
      )
      .then(({ data }) => data?.isEligible);
  }
  return false;
};
